import React, { useState } from "react";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PasswordFields } from "@/components/auth/PasswordFields";
import { useAuth } from "@/context/AuthContext";

/**
 * Mandatory first-login password change for an admin-created account.
 *
 * The `changeToken` is held in React memory ONLY (never localStorage) — it is a
 * single-use challenge token returned by a temp-password login. Exchanging it
 * (with the new password) at /auth/required-password-change revokes the temp
 * password, mints a brand-new session and signs the user in.
 */
export function RequiredPasswordChange({ changeToken, onSuccess, onExpired }) {
  const { completeRequiredPasswordChange } = useAuth();
  const [password, setPassword] = useState("");
  const [pwValid, setPwValid] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pwValid) {
      toast.error("Please meet all password requirements and confirm your password.");
      return;
    }
    setLoading(true);
    try {
      const user = await completeRequiredPasswordChange(changeToken, password);
      onSuccess(user);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not update your password. Please try again.";
      // An expired/invalid challenge means they must sign in again with the temp password.
      const expired = /expired|sign in again|no longer valid/i.test(detail);
      toast.error(detail);
      setPassword("");
      if (expired) onExpired?.(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="required-password-change-form">
      <div className="flex items-start gap-2 rounded-md border border-border bg-secondary/60 p-3 text-sm text-muted-foreground">
        <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <span>
          Your account was created by an administrator. For your security, choose a new
          password to finish signing in. Your temporary password will stop working once
          you set this.
        </span>
      </div>
      <PasswordFields
        password={password}
        setPassword={setPassword}
        onValidChange={setPwValid}
        idPrefix="rpc"
        label="New password"
      />
      <Button type="submit" className="w-full" disabled={loading || !pwValid} data-testid="required-password-change-submit">
        {loading ? "Saving…" : "Set new password & continue"}
      </Button>
    </form>
  );
}
